<template>
    <div class="configuration-import-preview">
        <!-- Metadata Section -->
        <div class="metadata-section mb-4" v-if="metadata">
            <h6>Import Source</h6>
            <b-row>
                <b-col md="6">
                    <dl class="row mb-0">
                        <dt class="col-sm-5">Source Instance:</dt>
                        <dd class="col-sm-7">{{ metadata.sourceInstance }}</dd>
                        <dt class="col-sm-5">Exported By:</dt>
                        <dd class="col-sm-7">{{ metadata.exportedBy }}</dd>
                    </dl>
                </b-col>
                <b-col md="6">
                    <dl class="row mb-0">
                        <dt class="col-sm-5">Exported At:</dt>
                        <dd class="col-sm-7">{{ formatDate(metadata.exportedAt) }}</dd>
                        <dt class="col-sm-5">Description:</dt>
                        <dd class="col-sm-7">{{ metadata.description || "(none)" }}</dd>
                    </dl>
                </b-col>
            </b-row>
        </div>

        <!-- Errors Alert -->
        <b-alert v-if="preview.errors.length > 0" show variant="danger">
            <strong>Cannot apply import due to errors:</strong>
            <ul class="mb-0 mt-2">
                <li v-for="(error, idx) in preview.errors" :key="'err-' + idx">{{ error }}</li>
            </ul>
        </b-alert>

        <!-- Warnings Alert -->
        <b-alert v-if="preview.warnings.length > 0" show variant="warning" class="mb-3">
            <strong>Warnings:</strong>
            <ul class="mb-0 mt-2">
                <li v-for="(warning, idx) in preview.warnings" :key="'warn-' + idx">{{ warning }}</li>
            </ul>
        </b-alert>

        <!-- No Changes Notice -->
        <b-alert v-if="hasNoChanges" show variant="info">
            <i class="fa fa-info-circle mr-2" />
            The imported configuration is identical to the current configuration. There are no changes to apply.
        </b-alert>

        <!-- Group Selection -->
        <div v-else class="group-selection mb-4">
            <h6>Select Groups to Import</h6>
            <p class="text-muted small">Select which configuration groups you want to import. Only groups with changes are shown.</p>
            <div class="d-flex flex-wrap" style="gap: 10px;">
                <b-form-checkbox v-for="group in groupsWithChanges" :key="group" v-model="selectedGroups"
                    :value="group" class="group-checkbox">
                    {{ group }}
                    <b-badge variant="primary" pill class="ml-1">{{ preview.changeCountsByGroup[group] }}</b-badge>
                </b-form-checkbox>
            </div>
            <div class="mt-2">
                <b-button size="sm" variant="outline-secondary" @click="selectAllGroups" class="mr-2">Select All</b-button>
                <b-button size="sm" variant="outline-secondary" @click="deselectAllGroups">Deselect All</b-button>
            </div>
        </div>

        <!-- Changes Table -->
        <template v-if="!hasNoChanges">
            <div class="changes-section" v-if="filteredChanges.length > 0">
                <h6>Changes Preview ({{ filteredChanges.length }} changes)</h6>
                <b-table :items="filteredChanges" :fields="changeFields" small striped hover responsive
                    class="changes-table" show-empty empty-text="No changes to show for selected groups.">
                    <template #cell(type)="data">
                        <b-badge :variant="getChangeTypeBadgeVariant(data.value)">
                            {{ formatChangeType(data.value) }}
                        </b-badge>
                    </template>
                    <template #cell(currentValue)="data">
                        <span class="value-cell" :title="data.value">{{ truncateValue(data.value) }}</span>
                    </template>
                    <template #cell(newValue)="data">
                        <span class="value-cell" :title="data.value">{{ truncateValue(data.value) }}</span>
                    </template>
                </b-table>
            </div>

            <b-alert v-else-if="selectedGroups.length === 0" show variant="info">
                Please select at least one group to import.
            </b-alert>

            <b-alert v-else show variant="success">
                No changes detected between the imported configuration and current configuration for the selected groups.
            </b-alert>
        </template>
    </div>
</template>

<script lang="ts">
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import {
    ConfigurationExportMetadata,
    ConfigurationImportPreview,
    ConfigurationChange,
    ChangeType,
    formatChangeType,
    getChangeTypeBadgeVariant,
} from "@/models/ConfigurationExport";

@Component({
    name: "ConfigurationImportPreview",
})
export default class ConfigurationImportPreviewComponent extends Vue {
    @Prop({ required: true }) preview!: ConfigurationImportPreview;
    @Prop({ required: false }) metadata?: ConfigurationExportMetadata;
    @Prop({ required: true }) availableGroups!: string[];
    @Prop({ required: true }) value!: string[]; // v-model for selected groups

    protected changeFields = [
        { key: "group", label: "Group", sortable: true },
        { key: "fieldName", label: "Field", sortable: true },
        { key: "type", label: "Change", sortable: true },
        { key: "currentValue", label: "Current Value" },
        { key: "newValue", label: "New Value" },
    ];

    get selectedGroups(): string[] {
        return this.value;
    }

    set selectedGroups(val: string[]) {
        this.$emit("input", val);
    }

    get groupsWithChanges(): string[] {
        return Object.keys(this.preview.changeCountsByGroup).sort();
    }

    get hasNoChanges(): boolean {
        return this.groupsWithChanges.length === 0;
    }

    get filteredChanges(): ConfigurationChange[] {
        if (this.selectedGroups.length === 0) {
            return [];
        }
        return this.preview.changes.filter(c => this.selectedGroups.includes(c.group));
    }

    formatDate(dateStr: string): string {
        if (!dateStr) return "";
        const date = new Date(dateStr);
        return date.toLocaleString();
    }

    formatChangeType(type: ChangeType): string {
        return formatChangeType(type);
    }

    getChangeTypeBadgeVariant(type: ChangeType): string {
        return getChangeTypeBadgeVariant(type);
    }

    truncateValue(value: string | null | undefined): string {
        if (value === null || value === undefined) return "(empty)";
        if (value.length > 100) {
            return value.substring(0, 97) + "...";
        }
        return value;
    }

    selectAllGroups() {
        this.selectedGroups = [...this.groupsWithChanges];
    }

    deselectAllGroups() {
        this.selectedGroups = [];
    }
}
</script>

<style scoped>
.metadata-section {
    background-color: #f8f9fa;
    padding: 15px;
    border-radius: 5px;
}

.metadata-section dl {
    margin-bottom: 0;
}

.metadata-section dt {
    font-weight: 600;
}

.metadata-section dd {
    margin-bottom: 0.25rem;
}

.group-checkbox {
    min-width: 150px;
}

.changes-table {
    max-height: 400px;
    overflow-y: auto;
}

.value-cell {
    max-width: 200px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    display: inline-block;
}
</style>
